import { RobotOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import ActionPreview from '@/components/Business/ActionPreview';
import EvidenceCard from '@/components/Business/EvidenceCard';
import { useBusiness } from '@/prototype/context';
import {
  contextKey,
  type ActionPreview as Preview,
} from '@/prototype/contracts';

import type { Kind } from '@/prototype/types';
import { EntityLink } from './components';

interface Answer {
  uuid: string;
  question: string;
  refs: string[];
  kind?: Kind;
}
export default function Assistant() {
  const { service, state } = useBusiness();
  const { message } = App.useApp();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [answers, setAnswers] = React.useState<Answer[]>([]);
  const [focus, setFocus] = React.useState<string>();
  const [preview, setPreview] = React.useState<Preview | null>(null);
  const [form] = Form.useForm();
  const scope = state
    ? contextKey(state.context) +
      state.entities
        .filter((e) => e.kind === 'policy' && e.status === 'published')
        .map((e) => e.uuid + e.revision)
        .join(',')
    : '';
  React.useEffect(() => {
    setAnswers([]);
    setFocus(undefined);
    setPreview(null);
  }, [scope]);
  React.useEffect(() => {
    setAnswers((previous) =>
      previous.filter((answer) =>
        answer.refs.every((id) => !!service?.get(id)),
      ),
    );
  }, [state?.revision, service]);
  React.useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (
        typeof detail?.question !== 'string' ||
        typeof detail?.entityUuid !== 'string' ||
        !service?.get(detail.entityUuid)
      )
        return;
      setText(detail.question);
      setFocus(detail.entityUuid);
      setOpen(true);
    };
    window.addEventListener('srbs:ask', listener);
    return () => window.removeEventListener('srbs:ask', listener);
  }, [service]);
  if (!service || !state) return null;
  const ask = () => {
    if (!text.trim()) return;
    let kind: Kind | undefined;
    if (/设备|工牌/.test(text)) kind = 'device';
    else if (/复核|线索/.test(text)) kind = 'clue';
    else if (/任务|待办/.test(text)) kind = 'task';
    else if (/异常|采集/.test(text)) kind = 'batch';
    else if (/案例|方法/.test(text)) kind = 'knowledge';
    else if (/录音|承诺|课堂|片段/.test(text)) kind = 'recording';
    let refs: string[] = [];
    const source = service.get(focus);
    if (source) refs = [source.uuid];
    else if (/然后|这些|上述|下一步/.test(text) && answers.length)
      refs = answers.at(-1)?.refs.filter((id) => !!service.get(id)) || [];
    else if (/承诺|教学|课堂/.test(text))
      refs = [
        ...new Set(
          service
            .search({ keyword: /承诺/.test(text) ? '承诺' : '教学' })
            .map((h) => h.entityUuid),
        ),
      ].slice(0, 5);
    else if (kind)
      refs = service
        .list(kind)
        .filter((e) =>
          kind === 'knowledge'
            ? e.status === 'published'
            : kind === 'batch'
              ? e.status === 'failed'
              : kind === 'clue'
                ? !['closed', 'excluded'].includes(e.status)
                : kind === 'task'
                  ? !['completed', 'closed', 'cancelled'].includes(e.status)
                  : true,
        )
        .slice(0, 5)
        .map((e) => e.uuid);
    setAnswers((previous) => [
      ...previous,
      { uuid: crypto.randomUUID(), question: text.trim(), refs, kind },
    ]);
    setText('');
    setFocus(undefined);
  };
  const selected = preview && service.get(preview.recommendation.sourceUuid);
  const buildInput = async () => {
    const values = await form.validateFields();
    return { memberUuid: values.owner, dueAt: values.dueAt?.toISOString() };
  };
  return (
    <>
      <Button
        className="srbs-assistant-trigger"
        type="primary"
        icon={<RobotOutlined />}
        onClick={() => setOpen(true)}
      >
        智能助手
      </Button>
      <Drawer
        title="智能助手"
        open={open}
        onClose={() => setOpen(false)}
        size="large"
      >
        <Tag>模拟数据 · 建议需人工确认</Tag>
        <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
          从当前授权的记录查找依据、解释下一步。可继续询问“这些记录下一步怎么处理”。
        </Typography.Paragraph>
        {!answers.length && (
          <Typography.Paragraph type="secondary">
            可询问：查录音、查待办、采集异常、已发布案例、沟通承诺。
          </Typography.Paragraph>
        )}
        {answers.map((answer) => {
          const refs = answer.refs.flatMap((id) => {
            const row = service.get(id);
            return row ? [row] : [];
          });
          const suggestions = service
            .suggestions()
            .filter((s) => refs.some((r) => r.uuid === s.sourceUuid));
          return (
            <section key={answer.uuid} className="srbs-surface">
              <Typography.Text strong>{answer.question}</Typography.Text>
              <Typography.Paragraph style={{ marginTop: 12 }}>
                {refs.length
                  ? `找到${refs.length}条可访问来源。请核对记录、业务状态和以下片段，再选择下一步行动；这些记录不自动形成正式处理结论。`
                  : '当前授权范围内没有匹配依据，请调整范围或问题。'}
              </Typography.Paragraph>
              {refs.map((row) => {
                const version = row.versions?.at(-1);
                const matched = service
                  .search({
                    keyword: /承诺/.test(answer.question) ? '承诺' : row.title,
                  })
                  .find((hit) => hit.entityUuid === row.uuid && hit.evidence);
                const segment =
                  version?.segments.find(
                    (seg) => seg.startMs === matched?.evidence?.startMs,
                  ) || version?.segments[0];
                return (
                  <div key={row.uuid} style={{ marginBottom: 16 }}>
                    <EntityLink uuid={row.uuid} />
                    {row.kind === 'recording' && version && segment && (
                      <EvidenceCard
                        evidence={{
                          recordingUuid: row.uuid,
                          transcriptVersionUuid: version.uuid,
                          startMs: segment.startMs,
                          endMs: segment.endMs,
                          quote: segment.text,
                        }}
                      />
                    )}
                  </div>
                );
              })}
              {suggestions.map((s) => (
                <Button
                  key={s.id}
                  disabled={!service.canExecute(s.action, s.sourceUuid)}
                  onClick={() => {
                    try {
                      setPreview(service.preview(s));
                    } catch {
                      message.warning('来源已变化，请重新查询');
                    }
                  }}
                  style={{ marginBottom: 8 }}
                >
                  {s.label} · 预览
                </Button>
              ))}
            </section>
          );
        })}
        <Input.TextArea
          aria-label="助手问题"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="例如：查找沟通承诺的依据"
        />
        <Space wrap style={{ marginTop: 12 }}>
          <Button type="primary" onClick={ask} disabled={!text.trim()}>
            查找依据
          </Button>
          <Button
            onClick={() => {
              setAnswers([]);
              setFocus(undefined);
            }}
          >
            清空对话
          </Button>
        </Space>
      </Drawer>
      <ActionPreview
        preview={preview}
        onClose={() => setPreview(null)}
        buildInput={buildInput}
      >
        <Form
          key={preview?.uuid}
          form={form}
          clearOnDestroy
          preserve={false}
          layout="vertical"
          initialValues={{ dueAt: dayjs(Date.parse(state.now) + 86400000) }}
        >
          <Form.Item
            name="owner"
            label="行动负责人"
            rules={[{ required: true, message: '请选择负责人' }]}
          >
            <Select
              options={service
                .members()
                .filter((e) => e.campus === selected?.campus)
                .map((e) => ({ value: e.uuid, label: e.title }))}
            />
          </Form.Item>
          <Form.Item
            name="dueAt"
            label="期限"
            rules={[{ required: true, message: '请选择期限' }]}
          >
            <DatePicker showTime />
          </Form.Item>
        </Form>
      </ActionPreview>
    </>
  );
}
